import React from 'react'
import { Link } from 'react-router-dom'
import { useLanguage } from '../i18n/LanguageContext'

export default function Footer() {
  const { lang, t, getPageUrl, getServiceUrl, getEquivalentPath } = useLanguage()

  const serviceKeys = [
    'specialtransport',
    'containerkoersel',
    'stykgods',
    'traepiller',
    'national-transport',
    'international-transport',
  ]

  const servicesList = serviceKeys.map((key) => ({
    key,
    path: getServiceUrl(key),
    title: t.servicesList[key]?.title || key,
  }))

  const daHref = getEquivalentPath('da')
  const enHref = getEquivalentPath('en')

  return (
    <footer className="bg-white text-slate-700 border-t border-slate-200 pt-16 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 pb-12 border-b border-slate-200">
          
          {/* Column 1: Company Profile */}
          <div className="space-y-4">
            <Link to={getPageUrl('home')} className="inline-block group">
              <img 
                src="https://assets.auxodev.dk/cdn-cgi/image/width=400,quality=90,format=auto/assets/ed865070-334e-4849-904d-48dc93d1adc7/mrou-transport-logo.png" 
                alt="M.Rou Transport Logo" 
                className="h-11 w-auto max-w-[220px] object-contain transition-transform group-hover:scale-[1.02]"
              />
            </Link>

            <p className="text-xs text-slate-500 leading-relaxed max-w-xs">
              {t.footer.companyDesc}
            </p>

            <div className="pt-2 flex items-center gap-4">
              <a 
                href="https://www.facebook.com/M.RouTransport/" 
                target="_blank" 
                rel="noopener noreferrer"
                aria-label="Facebook"
                className="text-slate-600 hover:text-sky-600 text-lg transition-colors"
              >
                <i className="fa-brands fa-facebook-f"></i>
              </a>
              <a 
                href="https://www.instagram.com/m.rou_transport/" 
                target="_blank" 
                rel="noopener noreferrer"
                aria-label="Instagram"
                className="text-slate-600 hover:text-sky-600 text-lg transition-colors"
              >
                <i className="fa-brands fa-instagram"></i>
              </a>
              <a 
                href="https://www.tiktok.com/@mroutransport" 
                target="_blank" 
                rel="noopener noreferrer"
                aria-label="TikTok"
                className="text-slate-600 hover:text-sky-600 text-lg transition-colors"
              >
                <i className="fa-brands fa-tiktok"></i>
              </a>
            </div>

            {/* Language Selector in Footer */}
            <div className="pt-3">
              <span className="text-[11px] text-slate-400 font-bold uppercase tracking-wider block mb-1.5">
                {t.nav.switchLang}:
              </span>
              <div className="inline-flex items-center border border-slate-200 bg-slate-50 p-0.5 text-xs font-semibold">
                <Link
                  to={daHref}
                  className={`px-3 py-1 transition-all ${
                    lang === 'da' ? 'bg-sky-600 text-white font-bold pointer-events-none' : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  🇩🇰 Dansk
                </Link>
                <Link
                  to={enHref}
                  className={`px-3 py-1 transition-all ${
                    lang === 'en' ? 'bg-sky-600 text-white font-bold pointer-events-none' : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  🇬🇧 English
                </Link>
              </div>
            </div>
          </div>

          {/* Column 2: Transport Ydelser */}
          <div>
            <h3 className="text-slate-900 font-bold text-base mb-4 tracking-wide uppercase text-xs">
              {t.footer.servicesHeading}
            </h3>
            <ul className="space-y-2.5 text-sm text-slate-600">
              {servicesList.map((srv) => (
                <li key={srv.key}>
                  <Link to={srv.path} className="hover:text-sky-600 transition-colors flex items-center gap-2">
                    <i className="fa-solid fa-chevron-right text-[10px] text-sky-500"></i>
                    <span>{srv.title}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3: Sider */}
          <div>
            <h3 className="text-slate-900 font-bold text-base mb-4 tracking-wide uppercase text-xs">
              {t.footer.quickLinksHeading}
            </h3>
            <ul className="space-y-2.5 text-sm text-slate-600">
              <li>
                <Link to={getPageUrl('home')} className="hover:text-sky-600 transition-colors flex items-center gap-2">
                  <i className="fa-solid fa-chevron-right text-[10px] text-sky-500"></i>
                  <span>{t.nav.home}</span>
                </Link>
              </li>
              <li>
                <Link to={getPageUrl('services')} className="hover:text-sky-600 transition-colors flex items-center gap-2">
                  <i className="fa-solid fa-chevron-right text-[10px] text-sky-500"></i>
                  <span>{t.nav.services}</span>
                </Link>
              </li>
              <li>
                <Link to={getPageUrl('gallery')} className="hover:text-sky-600 transition-colors flex items-center gap-2">
                  <i className="fa-solid fa-chevron-right text-[10px] text-sky-500"></i>
                  <span>{t.nav.gallery}</span>
                </Link>
              </li>
              <li>
                <Link to={getPageUrl('about')} className="hover:text-sky-600 transition-colors flex items-center gap-2">
                  <i className="fa-solid fa-chevron-right text-[10px] text-sky-500"></i>
                  <span>{t.nav.about}</span>
                </Link>
              </li>
              <li>
                <Link to={getPageUrl('contact')} className="hover:text-sky-600 transition-colors flex items-center gap-2">
                  <i className="fa-solid fa-chevron-right text-[10px] text-sky-500"></i>
                  <span>{t.nav.contact}</span>
                </Link>
              </li>
            </ul>
          </div>

          {/* Column 4: Virksomhedsoplysninger */}
          <div>
            <h3 className="text-slate-900 font-bold text-base mb-4 tracking-wide uppercase text-xs">
              {t.footer.contactHeading}
            </h3>
            <div className="space-y-3 text-sm text-slate-600">
              <div className="flex items-start gap-3">
                <i className="fa-solid fa-building text-sky-500 mt-1"></i>
                <div>
                  <span className="font-bold text-slate-900 block">M.Rou Transport</span>
                  <span className="text-slate-500">CVR: 42145572</span>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <i className="fa-solid fa-location-dot text-sky-500 mt-1"></i>
                <span>Skibbyvej 35, 8220 Brabrand</span>
              </div>

              <div className="flex items-center gap-3">
                <i className="fa-solid fa-phone text-sky-500"></i>
                <a 
                  href="tel:+4525303016" 
                  className="hover:text-sky-600 transition-colors font-medium whitespace-nowrap text-slate-800"
                >
                  <span className="whitespace-nowrap">+45 25 30 30 16</span>
                </a>
              </div>

              <div className="flex items-center gap-3">
                <i className="fa-solid fa-envelope text-sky-500"></i>
                <a 
                  href="mailto:mr@mrou.dk" 
                  className="hover:text-sky-600 transition-colors font-medium whitespace-nowrap text-slate-800"
                >
                  <span className="whitespace-nowrap">mr@mrou.dk</span>
                </a>
              </div>
            </div>
          </div>

        </div>

        {/* Bottom Copyright */}
        <div className="pt-8 flex flex-col sm:flex-row items-center justify-between text-xs text-slate-500 gap-4">
          <p>© {new Date().getFullYear()} M.Rou Transport. {t.footer.allRights} CVR: 42145572.</p>
          <div className="flex items-center gap-6 text-slate-500">
            <span>{t.footer.designedWith}</span>
          </div>
        </div>
      </div>
    </footer>
  )
}
